export type CameraAccessFailureCode =
  | 'not-found'
  | 'permission-denied'
  | 'device-busy'
  | 'unsupported'
  | 'unknown';

export interface CameraAccessFailure {
  code: CameraAccessFailureCode;
  title: string;
  message: string;
  guidance: readonly string[];
}

const CAMERA_FAILURES: Record<CameraAccessFailureCode, CameraAccessFailure> = {
  'not-found': {
    code: 'not-found',
    title: 'No Camera Detected',
    message: 'Chrome could not find an available camera. The session can continue without video analysis.',
    guidance: [
      'Connect or enable a camera on this device.',
      'Open Chrome camera settings and select the correct device.',
      'Click “Try Again” after the camera appears.',
    ],
  },
  'permission-denied': {
    code: 'permission-denied',
    title: 'Camera Permission Required',
    message: 'Camera access is blocked for this site. The session can continue without video analysis.',
    guidance: [
      'Open the site controls beside the browser address.',
      'Set Camera permission to Allow.',
      'Click “Try Again” after changing the permission.',
    ],
  },
  'device-busy': {
    code: 'device-busy',
    title: 'Camera Is In Use',
    message: 'Another application is using the camera. The session can continue without video analysis.',
    guidance: [
      'Close other video or meeting applications.',
      'Check that another browser tab is not using the camera.',
      'Click “Try Again” when the camera is free.',
    ],
  },
  unsupported: {
    code: 'unsupported',
    title: 'Camera Not Supported',
    message: 'This browser cannot access a camera. The session can continue without video analysis.',
    guidance: [
      'Use a current version of Chrome or Edge.',
      'Open the simulation from a secure HTTPS address.',
      'Click “Try Again” after changing browser.',
    ],
  },
  unknown: {
    code: 'unknown',
    title: 'Camera Unavailable',
    message: 'The camera could not be started. The session can continue without video analysis.',
    guidance: [
      'Check the selected camera in browser settings.',
      'Reconnect the camera or restart the browser.',
      'Click “Try Again” when ready.',
    ],
  },
};

function getErrorName(error: unknown): string {
  return error instanceof Error ? error.name : '';
}

export function getCameraAccessFailure(error: unknown): CameraAccessFailure {
  switch (getErrorName(error)) {
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return CAMERA_FAILURES['not-found'];
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return CAMERA_FAILURES['permission-denied'];
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return CAMERA_FAILURES['device-busy'];
    case 'TypeError':
      return CAMERA_FAILURES.unsupported;
    default:
      return CAMERA_FAILURES.unknown;
  }
}

function shouldRetryWithoutConstraints(error: unknown): boolean {
  const name = getErrorName(error);
  return name === 'NotFoundError'
    || name === 'DevicesNotFoundError'
    || name === 'OverconstrainedError'
    || name === 'ConstraintNotSatisfiedError';
}

export async function requestUserCameraStream(
  preferredVideo: MediaTrackConstraints,
): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new DOMException('Camera access is not supported by this browser.', 'TypeError');
  }

  try {
    return await navigator.mediaDevices.getUserMedia({ video: preferredVideo, audio: false });
  } catch (error) {
    if (!shouldRetryWithoutConstraints(error)) throw error;
    return navigator.mediaDevices.getUserMedia({ video: true, audio: false });
  }
}
